import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ShieldCheck,
  Layers,
  Eye,
  Target,
  TrendingUp,
  QrCode,
  Handshake,
  Sparkles,
  ArrowRight,
  RotateCcw,
} from "lucide-react";
import { translations } from "../lib/translations";
import { track } from "../lib/analytics";
import { TopBar, Shell } from "../components/funnel/Shell";
import { OptionCard } from "../components/funnel/OptionCard";
import { PrimaryButton, SecondaryButton } from "../components/funnel/PrimaryButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIGNUP_BASE = "https://channelpartner.network/signup";
const SITE_URL = "https://channelpartner.network";

// Step keys
const STEPS = [
  "intro", // 1 of 7 (intro is preface, but counted as step 1)
  "qualify",
  "role",
  "property",
  "enquiry",
  "challenge",
  "diagnosis",
  "solution",
  "final",
];

const PROGRESS_STEPS = ["qualify", "role", "property", "enquiry", "challenge", "diagnosis", "solution"];

const pageVariants = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};
const pageTransition = { type: "spring", stiffness: 280, damping: 30 };

const buildSignupUrl = (answers) => {
  const params = new URLSearchParams();
  params.set("source", "diagnostic");
  if (answers.name) params.set("name", answers.name);
  if (answers.role) params.set("role", answers.role);
  if (answers.propertyTypes?.length)
    params.set("property", answers.propertyTypes.join(","));
  if (answers.enquirySources?.length)
    params.set("enquiry", answers.enquirySources.join(","));
  if (answers.challenge) params.set("challenge", answers.challenge);
  if (answers.language) params.set("lang", answers.language);
  return `${SIGNUP_BASE}?${params.toString()}`;
};

export default function Funnel() {
  const [lang, setLang] = useState("hinglish");
  const t = translations[lang];

  const [step, setStep] = useState("intro");
  const [history, setHistory] = useState([]); // stack for back nav
  const [exitView, setExitView] = useState(null); // "qualify" | "soft" | null

  const [name, setName] = useState("");
  const [activeRealEstate, setActiveRealEstate] = useState(null); // yes/no
  const [role, setRole] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [enquirySources, setEnquirySources] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [joinIntent, setJoinIntent] = useState(null);

  // Fire diagnostic_started once on mount
  const startedRef = React.useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("diagnostic_started", { language: lang });
  }, [lang]);

  const answers = useMemo(
    () => ({
      name,
      language: lang,
      activeRealEstate,
      role,
      propertyTypes,
      enquirySources,
      challenge,
      joinIntent,
    }),
    [name, lang, activeRealEstate, role, propertyTypes, enquirySources, challenge, joinIntent],
  );

  // Progress: based on PROGRESS_STEPS index
  const progressInfo = useMemo(() => {
    if (exitView || step === "intro") return { value: 0, label: null, totalIdx: 0, total: PROGRESS_STEPS.length };
    const idx = PROGRESS_STEPS.indexOf(step);
    if (idx === -1)
      return { value: 100, label: null, totalIdx: PROGRESS_STEPS.length, total: PROGRESS_STEPS.length };
    return {
      value: ((idx + 1) / PROGRESS_STEPS.length) * 100,
      label: t.common.stepOf(idx + 1, PROGRESS_STEPS.length),
      totalIdx: idx + 1,
      total: PROGRESS_STEPS.length,
    };
  }, [step, exitView, t.common]);

  const goNext = (next) => {
    setHistory((h) => [...h, { step, exitView }]);
    setStep(next);
    setExitView(null);
  };

  const goBack = () => {
    setHistory((h) => {
      const prev = h[h.length - 1];
      if (prev) {
        setStep(prev.step);
        setExitView(prev.exitView ?? null);
        return h.slice(0, -1);
      }
      return h;
    });
  };

  const restart = () => {
    setHistory([]);
    setStep("intro");
    setExitView(null);
    setActiveRealEstate(null);
    setRole(null);
    setPropertyTypes([]);
    setEnquirySources([]);
    setChallenge(null);
    setJoinIntent(null);
    track("diagnostic_started", { language: lang, restart: true });
  };

  // ===== Persistence (best-effort, fire-and-forget) =====
  const persistDiagnostic = async (completedFlag, intent) => {
    try {
      await axios.post(`${API}/diagnostic`, {
        name: name || null,
        language: lang,
        active_real_estate: activeRealEstate,
        role,
        property_types: propertyTypes,
        enquiry_sources: enquirySources,
        challenge,
        join_intent: intent ?? joinIntent,
        completed: completedFlag,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
        referrer: typeof document !== "undefined" ? document.referrer : null,
      });
    } catch (e) {
      // silent — don't block UX
      console.warn("diagnostic save failed", e?.message);
    }
  };

  // ===== Handlers =====
  const handleStart = () => {
    track("diagnostic_started", { language: lang, name_provided: !!name });
    goNext("qualify");
  };

  const handleQualify = (value) => {
    setActiveRealEstate(value);
    if (value === "yes") {
      track("active_real_estate_yes");
      goNext("role");
    } else {
      track("active_real_estate_no");
      setHistory((h) => [...h, { step, exitView }]);
      setExitView("qualify");
      persistDiagnostic(false, null);
    }
  };

  const handleRole = () => {
    if (!role) return;
    track("role_selected", { role });
    goNext("property");
  };

  const togglePropertyType = (v) => {
    setPropertyTypes((prev) => {
      if (v === "multiple") return ["multiple"];
      const without = prev.filter((x) => x !== "multiple");
      return without.includes(v)
        ? without.filter((x) => x !== v)
        : [...without, v];
    });
  };

  const handleProperty = () => {
    if (!propertyTypes.length) return;
    track("property_type_selected", { propertyTypes });
    goNext("enquiry");
  };

  const toggleEnquiry = (v) => {
    setEnquirySources((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  };

  const handleEnquiry = () => {
    if (!enquirySources.length) return;
    track("enquiry_source_selected", { enquirySources });
    goNext("challenge");
  };

  const handleChallenge = () => {
    if (!challenge) return;
    track("challenge_selected", { challenge });
    track("diagnosis_viewed", { challenge });
    goNext("diagnosis");
  };

  const handleDiagnosisContinue = () => goNext("solution");

  const handleSolution = (value) => {
    setJoinIntent(value);
    if (value === "yes") {
      persistDiagnostic(true, "yes");
      goNext("final");
    } else {
      track("not_now_clicked", { stage: "solution" });
      persistDiagnostic(true, "no");
      setHistory((h) => [...h, { step, exitView }]);
      setExitView("soft");
    }
  };

  const handleSignup = () => {
    track("signup_clicked", answers);
    const url = buildSignupUrl({ ...answers, joinIntent: "yes" });
    window.location.href = url;
  };

  // ===== Layout vars =====
  const showBack = step !== "intro" && !(exitView === "qualify");
  const showTopBar = true;

  // ===== Renderers =====
  const renderIntro = () => (
    <motion.div
      key="intro"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-7"
    >
      <div className="inline-flex items-center gap-2 self-start rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-3 py-1.5 text-xs font-semibold tracking-wide">
        <Sparkles className="w-3.5 h-3.5" />
        2-MINUTE DIAGNOSTIC
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-[1.15]">
        {t.intro.heading}
      </h1>
      <p className="text-[15px] md:text-base text-[#475569] leading-relaxed">
        {t.intro.sub}
      </p>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="name-input"
          className="text-sm font-semibold text-[#0F172A]"
        >
          {t.intro.nameLabel}
        </label>
        <input
          id="name-input"
          data-testid="name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.intro.namePlaceholder}
          className="w-full px-4 py-4 rounded-2xl glass-input focus:outline-none focus:border-[#FF5E1A] focus:ring-2 focus:ring-[#FF5E1A]/20 text-base text-[#0F172A] placeholder:text-slate-500 transition-all"
        />
      </div>

      <PrimaryButton onClick={handleStart} testId="start-diagnostic-btn">
        {t.intro.start}
      </PrimaryButton>
      <p className="text-xs text-gray-500 text-center -mt-2">
        {t.common.builtFor}
      </p>
    </motion.div>
  );

  const renderQualify = () => (
    <motion.div
      key="qualify"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A] leading-tight">
        {t.s2.question}
      </h2>
      <div className="flex flex-col gap-3">
        {t.s2.options.map((opt) => (
          <OptionCard
            key={opt.value}
            label={opt.label}
            selected={activeRealEstate === opt.value}
            onClick={() => handleQualify(opt.value)}
            testId={`qualify-option-${opt.value}`}
          />
        ))}
      </div>
    </motion.div>
  );

  const renderExitQualify = () => (
    <motion.div
      key="exit-qualify"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <div className="rounded-2xl glass p-6 border-[#FFB088]/40 bg-[#FFF3ED]/55">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A] leading-tight">
          {t.exit.heading}
        </h2>
        <p className="text-[15px] text-[#475569] leading-relaxed mt-3">
          {t.exit.text}
        </p>
      </div>
      <PrimaryButton
        onClick={() => {
          window.location.href = SITE_URL;
        }}
        testId="exit-visit-btn"
      >
        {t.exit.cta}
      </PrimaryButton>
      <SecondaryButton onClick={restart} testId="exit-restart-btn">
        <span className="inline-flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          {t.common.restart}
        </span>
      </SecondaryButton>
    </motion.div>
  );

  const renderRole = () => (
    <motion.div
      key="role"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A] leading-tight">
        {t.s3.question}
      </h2>
      <p className="text-sm text-gray-500 -mt-3">{t.common.selectOne}</p>
      <div className="flex flex-col gap-3">
        {t.s3.options.map((opt) => (
          <OptionCard
            key={opt.value}
            label={opt.label}
            selected={role === opt.value}
            onClick={() => setRole(opt.value)}
            testId={`role-option-${opt.value}`}
          />
        ))}
      </div>
      <PrimaryButton
        onClick={handleRole}
        disabled={!role}
        testId="role-continue-btn"
      >
        {t.common.continue}
      </PrimaryButton>
    </motion.div>
  );

  const renderProperty = () => (
    <motion.div
      key="property"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A] leading-tight">
        {t.s4.question}
      </h2>
      <p className="text-sm text-gray-500 -mt-3">{t.s4.hint}</p>
      <div className="flex flex-col gap-3">
        {t.s4.options.map((opt) => (
          <OptionCard
            key={opt.value}
            label={opt.label}
            multi
            selected={propertyTypes.includes(opt.value)}
            onClick={() => togglePropertyType(opt.value)}
            testId={`property-option-${opt.value}`}
          />
        ))}
      </div>
      <PrimaryButton
        onClick={handleProperty}
        disabled={!propertyTypes.length}
        testId="property-continue-btn"
      >
        {t.common.continue}
      </PrimaryButton>
    </motion.div>
  );

  const renderEnquiry = () => (
    <motion.div
      key="enquiry"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A] leading-tight">
        {t.s5.question}
      </h2>
      <p className="text-sm text-gray-500 -mt-3">{t.s5.hint}</p>
      <div className="flex flex-col gap-3">
        {t.s5.options.map((opt) => (
          <OptionCard
            key={opt.value}
            label={opt.label}
            multi
            selected={enquirySources.includes(opt.value)}
            onClick={() => toggleEnquiry(opt.value)}
            testId={`enquiry-option-${opt.value}`}
          />
        ))}
      </div>
      <PrimaryButton
        onClick={handleEnquiry}
        disabled={!enquirySources.length}
        testId="enquiry-continue-btn"
      >
        {t.common.continue}
      </PrimaryButton>
    </motion.div>
  );

  const renderChallenge = () => (
    <motion.div
      key="challenge"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0F172A] leading-tight">
        {t.s6.question}
      </h2>
      <p className="text-sm text-gray-500 -mt-3">{t.common.selectOne}</p>
      <div className="flex flex-col gap-3">
        {t.s6.options.map((opt) => (
          <OptionCard
            key={opt.value}
            label={opt.label}
            selected={challenge === opt.value}
            onClick={() => setChallenge(opt.value)}
            testId={`challenge-option-${opt.value}`}
          />
        ))}
      </div>
      <PrimaryButton
        onClick={handleChallenge}
        disabled={!challenge}
        testId="challenge-continue-btn"
      >
        {t.s6.cta}
      </PrimaryButton>
    </motion.div>
  );

  const renderDiagnosis = () => {
    const d = challenge ? t.diagnoses[challenge] : null;
    if (!d) return null;
    return (
      <motion.div
        key="diagnosis"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={pageTransition}
        className="flex flex-col gap-6"
        data-testid="diagnosis-screen"
      >
        <div className="rounded-2xl border-2 border-[#FF5E1A] bg-[#FFF3ED]/85 backdrop-blur-md p-6 cp-shadow-orange">
          <p className="text-xs uppercase tracking-[0.15em] text-[#FF5E1A] font-bold">
            {d.leakageLabel}
          </p>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#FF5E1A] mt-1">
            {d.leakageName}
          </h3>
        </div>

        <h2 className="font-heading text-xl md:text-2xl font-semibold text-[#0F172A] leading-snug">
          {d.heading}
        </h2>
        <p className="text-[15px] text-[#475569] leading-relaxed">{d.text}</p>

        <div className="rounded-2xl p-6 text-white relative overflow-hidden"
             style={{
               background: "linear-gradient(135deg, #FF5E1A 0%, #E04F00 55%, #B83A00 100%)",
               boxShadow: "0 20px 40px -16px rgba(255, 94, 26, 0.45), 0 2px 0 rgba(255,255,255,0.18) inset",
             }}>
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/85 font-bold relative">
            ChannelPartner.Network
          </p>
          <p className="text-[15px] leading-relaxed text-white mt-2 relative">
            {d.solution}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold border border-white/30 relative">
            <Sparkles className="w-3.5 h-3.5" />
            {d.benefit}
          </div>
        </div>

        <PrimaryButton
          onClick={handleDiagnosisContinue}
          testId="diagnosis-continue-btn"
        >
          {t.common.continue}
        </PrimaryButton>
      </motion.div>
    );
  };

  const benefitIcons = [
    ShieldCheck,
    Layers,
    Eye,
    Target,
    TrendingUp,
    QrCode,
    Handshake,
  ];

  const renderSolution = () => (
    <motion.div
      key="solution"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] leading-tight">
        {t.s8.heading}
      </h2>
      <p className="text-[15px] text-[#475569] leading-relaxed">{t.s8.text}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {t.s8.benefits.map((b, i) => {
          const Icon = benefitIcons[i] || Sparkles;
          return (
            <div
              key={i}
              className="p-4 rounded-2xl glass flex items-start gap-3"
              data-testid={`benefit-card-${i}`}
            >
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#FFF3ED] text-[#FF5E1A] flex items-center justify-center">
                <Icon className="w-4.5 h-4.5" />
              </div>
              <p className="text-sm font-semibold text-[#0F172A] leading-snug">
                {b}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-2">
        <h3 className="font-heading text-xl font-semibold text-[#0F172A]">
          {t.s8.question}
        </h3>
        <div className="flex flex-col gap-3 mt-4">
          {t.s8.options.map((opt) => (
            <OptionCard
              key={opt.value}
              label={opt.label}
              selected={joinIntent === opt.value}
              onClick={() => handleSolution(opt.value)}
              testId={`solution-option-${opt.value}`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );

  const renderFinal = () => (
    <motion.div
      key="final"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <div className="rounded-2xl p-6 text-white relative overflow-hidden"
           style={{
             background: "linear-gradient(135deg, #FF5E1A 0%, #E04F00 55%, #B83A00 100%)",
             boxShadow: "0 24px 48px -18px rgba(255, 94, 26, 0.5), 0 2px 0 rgba(255,255,255,0.2) inset",
           }}>
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-white/12 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/8 blur-3xl pointer-events-none" />
        <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold border border-white/30 relative">
          <Sparkles className="w-3.5 h-3.5" />
          {name ? `${name}, you're ready` : "You're ready"}
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4 leading-tight relative">
          {t.finalSignup.heading}
        </h2>
        <p className="text-[15px] text-white/95 leading-relaxed mt-3 relative">
          {t.finalSignup.text}
        </p>
      </div>

      <PrimaryButton onClick={handleSignup} testId="signup-now-btn">
        {t.finalSignup.cta}
      </PrimaryButton>
      <button
        type="button"
        onClick={restart}
        data-testid="final-restart-btn"
        className="text-sm text-gray-500 hover:text-[#FF5E1A] inline-flex items-center justify-center gap-2 mx-auto transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        {t.common.restart}
      </button>
    </motion.div>
  );

  const renderSoftExit = () => (
    <motion.div
      key="soft-exit"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="flex flex-col gap-6"
    >
      <div className="rounded-2xl glass p-6">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A]">
          {t.softExit.heading}
        </h2>
        <p className="text-[15px] text-[#475569] leading-relaxed mt-3">
          {t.softExit.text}
        </p>
      </div>
      <PrimaryButton
        onClick={() => {
          window.location.href = SITE_URL;
        }}
        testId="soft-exit-visit-btn"
      >
        {t.softExit.cta}
      </PrimaryButton>
      <SecondaryButton onClick={restart} testId="soft-exit-restart-btn">
        <span className="inline-flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          {t.softExit.restart}
        </span>
      </SecondaryButton>
    </motion.div>
  );

  // Decide what to render
  const renderCurrent = () => {
    if (exitView === "qualify") return renderExitQualify();
    if (exitView === "soft") return renderSoftExit();
    switch (step) {
      case "intro":
        return renderIntro();
      case "qualify":
        return renderQualify();
      case "role":
        return renderRole();
      case "property":
        return renderProperty();
      case "enquiry":
        return renderEnquiry();
      case "challenge":
        return renderChallenge();
      case "diagnosis":
        return renderDiagnosis();
      case "solution":
        return renderSolution();
      case "final":
        return renderFinal();
      default:
        return renderIntro();
    }
  };

  return (
    <>
      {showTopBar && (
        <TopBar
          showBack={showBack && history.length > 0}
          onBack={goBack}
          stepLabel={progressInfo.label}
          progress={
            step === "intro" || exitView ? null : progressInfo.value
          }
          lang={lang}
          setLang={setLang}
        />
      )}
      <Shell>
        <AnimatePresence mode="wait">{renderCurrent()}</AnimatePresence>
      </Shell>
    </>
  );
}
